import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Search, Factory, Layers } from "lucide-react";

const EMPTY = { all_plants: false, all_segments: false, plant_ids: [], region_ids: [], segments: [], account_ids: [] };

export function ScopePicker({ value, onChange, testid = "scope-picker" }) {
  const { t, picklists } = useApp();
  const scope = { ...EMPTY, ...(value || {}) };
  const [plants, setPlants] = useState([]);
  const [regions, setRegions] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/iam/plants?page_size=200").then((r) => setPlants(r.data.items || [])).catch(() => {});
    api.get("/iam/regions").then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);

  const set = (patch) => onChange({ ...scope, ...patch });
  const segments = picklists?.segments || [];

  const grouped = useMemo(() => {
    const filtered = plants.filter((p) => {
      const s = `${p.code} ${p.name} ${p.city || ""}`.toLowerCase();
      return !q || s.includes(q.toLowerCase());
    });
    const map = {};
    filtered.forEach((p) => {
      const key = p.region_name || "–";
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [plants, q]);

  const effective = useMemo(() => {
    const ids = new Set(scope.plant_ids);
    plants.forEach((p) => { if (scope.region_ids.includes(p.region_id)) ids.add(p.id); });
    return plants.filter((p) => ids.has(p.id));
  }, [plants, scope.plant_ids, scope.region_ids]);

  const togglePlant = (id) => set({
    plant_ids: scope.plant_ids.includes(id) ? scope.plant_ids.filter((x) => x !== id) : [...scope.plant_ids, id],
  });
  const toggleRegion = (id) => set({
    region_ids: scope.region_ids.includes(id) ? scope.region_ids.filter((x) => x !== id) : [...scope.region_ids, id],
  });
  const toggleSegment = (s) => set({
    segments: scope.segments.includes(s) ? scope.segments.filter((x) => x !== s) : [...scope.segments, s],
  });

  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5">
            <Factory className="h-4 w-4 text-[#E8871E]" />{t("iam_plants")}
          </p>
          <label className="flex items-center gap-2 text-xs">
            {t("iam_all_plants")}
            <Switch checked={scope.all_plants} onCheckedChange={(v) => set({ all_plants: v })}
              data-testid="scope-all-plants" />
          </label>
        </div>
        {!scope.all_plants && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {regions.map((r) => (
                <button key={r.id} type="button" onClick={() => toggleRegion(r.id)}
                  data-testid={`scope-region-${r.id}`}
                  className={`px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-colors ${
                    scope.region_ids.includes(r.id)
                      ? "bg-[#1B2A4A] text-white border-[#1B2A4A]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#E8871E]"}`}>
                  {t("iam_select_region")}: {r.name}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("iam_search_plants")}
                className="pl-8 h-9" data-testid="scope-plant-search" />
            </div>
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 rounded-md border border-gray-100">
              {Object.entries(grouped).map(([region, list]) => (
                <div key={region}>
                  <p className="px-3 py-1.5 bg-gray-50/70 text-[10px] uppercase tracking-wider text-gray-500">{region}</p>
                  {list.map((p) => (
                    <label key={p.id} data-testid={`scope-plant-${p.code}`}
                      className="px-3 py-2 flex items-center gap-2 text-sm cursor-pointer hover:bg-amber-50/40">
                      <Checkbox checked={scope.plant_ids.includes(p.id)} onCheckedChange={() => togglePlant(p.id)} />
                      <span className="font-mono text-xs text-[#E8871E]">{p.code}</span>
                      <span className="truncate">{p.name}</span>
                      <span className="ml-auto text-[11px] text-gray-400">{p.country}</span>
                    </label>
                  ))}
                </div>
              ))}
              {!Object.keys(grouped).length && <p className="p-3 text-xs text-gray-400">–</p>}
            </div>
            <div className="text-[11px] text-gray-500" data-testid="scope-effective-plants">
              {t("iam_effective")}: {effective.length ? effective.map((p) => p.code).join(", ") : "–"}
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5">
            <Layers className="h-4 w-4 text-[#E8871E]" />{t("iam_segments")}
          </p>
          <label className="flex items-center gap-2 text-xs">
            {t("iam_all_segments")}
            <Switch checked={scope.all_segments} onCheckedChange={(v) => set({ all_segments: v })}
              data-testid="scope-all-segments" />
          </label>
        </div>
        {!scope.all_segments && (
          <div className="flex flex-wrap gap-1.5" data-testid="scope-segment-list">
            {segments.map((s) => (
              <button key={s} type="button" onClick={() => toggleSegment(s)}
                data-testid={`scope-segment-${s}`}
                className={`px-2.5 h-7 rounded-full text-[11px] font-medium border transition-colors ${
                  scope.segments.includes(s)
                    ? "bg-[#E8871E] text-white border-[#E8871E]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#E8871E]"}`}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ScopeSummary({ scope, plants = [] }) {
  const { t } = useApp();
  const s = { ...EMPTY, ...(scope || {}) };
  const codes = plants.filter((p) => s.plant_ids.includes(p.id)).map((p) => p.code);
  return (
    <div className="flex flex-wrap gap-1.5 text-[11px]">
      <Badge variant="outline">{s.all_plants ? t("iam_all_plants") : `${t("iam_plants")}: ${codes.join(", ") || "–"}`}</Badge>
      <Badge variant="outline">{s.all_segments ? t("iam_all_segments") : `${t("iam_segments")}: ${(s.segments || []).join(", ") || "–"}`}</Badge>
    </div>
  );
}

export function InlineSave({ onSave, saving, label }) {
  return (
    <Button onClick={onSave} disabled={saving} data-testid="scope-save-btn"
      className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white">{saving ? "…" : label}</Button>
  );
}

export { EMPTY as EMPTY_SCOPE };
